import React, { useState, useEffect } from 'react';
import { getSalesScreenOptions } from '../../../services/migration/salesScreenDataService';
import { getCashboxesMap, getBankAccountsMap } from '../../../services/migration/financialDataService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  TextField,
  Grid,
  FormHelperText,
  Autocomplete,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { useAuth } from '../../../contexts/AuthContext';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import GuideButton from '../../common/guide/GuideButton';

// Ödeme yöntemleri
const PAYMENT_METHOD_NAMES: {[key: string]: string} = {
  'cash': 'Nakit',
  'credit_card': 'Kredi Kartı',
  'bank_transfer': 'Havale/EFT',
  'check': 'Çek',
  'pos': 'Pos'
};

// İşlem arayüzü
interface Transaction {
  id: string;
  accountId: string;
  accountName?: string;
  type: string;
  transactionType?: string;
  operationType?: string;
  amount: number;
  debitAmount?: number;
  creditAmount?: number;
  debitBalance?: number;
  description: string;
  documentNo?: string;
  referenceId?: string;
  referenceType?: string;
  date?: string;
  createdAt: string;
  createdBy?: string;
  isIncome: boolean;
  currency?: string;
  exchangeRate?: number;
  paymentMethod?: string;
  previousBalance?: number;
  nextBalance?: number;
  cashboxId?: string;
  cashboxName?: string;
  bankAccountId?: string;
  bankAccountName?: string;
}

// Kasa arayüzü
interface Cashbox {
  id: string;
  name: string;
  balance: number;
  currency: string;
  isActive?: boolean;
}

// Banka hesabı arayüzü
interface BankAccount {
  id: string;
  bankName: string;
  accountName?: string;
  balance: number;
  currency: string;
  isActive?: boolean;
}

// Satış ekranı arayüzü
interface PosDevice {
  id: string;
  name: string;
  type: string; // 'pos' veya 'quicksale' tipi
  isActive?: boolean;
}

// Props arayüzü
interface TransactionPaymentMethodChangeModalProps {
  open: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  companyId: string | null;
  onSuccess?: () => void;
}

// İşlem adımları
const steps = [
  'İşlem bilgilerini kontrol et',
  'Hedef hesabı seç',
  'Onay',
  'İşlem tamamlanıyor'
];

const TransactionPaymentMethodChangeModal: React.FC<TransactionPaymentMethodChangeModalProps> = ({
  open,
  onClose,
  transaction,
  companyId,
  onSuccess
}) => {
  const { userDetails } = useAuth();
  
  // State tanımlamaları
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processLog, setProcessLog] = useState<string[]>([]);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Hedef hesap seçimleri
  const [cashboxes, setCashboxes] = useState<Cashbox[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedCashbox, setSelectedCashbox] = useState<Cashbox | null>(null);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
  
  // Satış ekranı seçimleri
  const [posDevices, setPosDevices] = useState<PosDevice[]>([]);
  const [selectedPosDevice, setSelectedPosDevice] = useState<PosDevice | null>(null);
  
  // İsteğe bağlı açıklama
  const [description, setDescription] = useState('');
  
  // Mevcut ödeme yöntemi
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<string>('');
  const [targetPaymentMethod, setTargetPaymentMethod] = useState<string>('');
  
  // İşlem başladığında state'leri sıfırla
  useEffect(() => {
    if (open) {
      console.log("Modal açıldı - transaction:", transaction);
      
      // State'leri sıfırla
      setActiveStep(0);
      setError(null);
      setProcessLog([]);
      setIsSuccess(false);
      setDescription('');
      setSelectedPosDevice(null);
      
      if (transaction) {
        // Mevcut ödeme yöntemini belirle
        const currentMethod = transaction.paymentMethod || 'cash';
        setCurrentPaymentMethod(currentMethod);
        console.log("Mevcut ödeme yöntemi:", currentMethod, PAYMENT_METHOD_NAMES[currentMethod]);
        
        // Hedef ödeme yöntemini belirle (tam tersi)
        const isCurrentCash = currentMethod === 'cash';
        const targetMethod = isCurrentCash ? 'pos' : 'cash';
        setTargetPaymentMethod(targetMethod);
        console.log("Hedef ödeme yöntemi:", targetMethod, PAYMENT_METHOD_NAMES[targetMethod]);
        
        // Veri yükleme
        if (companyId) {
          // Önce kasaları ve banka hesaplarını yükle
          loadCashboxes();
          loadBankAccounts();
          loadPosDevices();
        }
      } else {
        console.warn("Modal açıldı fakat transaction verisi yok!");
      }
    }
  }, [open, transaction, companyId]);
  
  // Banka ve kasa verileri yüklendikten sonra ilgili hareket kaydını bul
  useEffect(() => {
    if (open && transaction && companyId && cashboxes.length > 0 && bankAccounts.length > 0) {
      console.log("Banka ve kasa verileri yüklendi, ilgili hareket kaydı aranıyor...");
      findRelatedTransactions();
    }
  }, [open, transaction, companyId, cashboxes.length, bankAccounts.length]);
  
  // İlgili banka/kasa hareket kayıtlarını bul
  const findRelatedTransactions = async () => {
    if (!transaction || !companyId) return;
    
    try {
      setLoading(true);
      
      // Mevcut ödeme yöntemi ve ilgili hareket kaydını kontrol et
      const isCurrentCash = (transaction.paymentMethod || 'cash') === 'cash';
      
      if (isCurrentCash) {
        // Nakit -> POS: Kasa hareket kaydını bul
        if (transaction.cashboxId) {
          // Kasayı otomatik seç
          const cashbox = cashboxes.find(c => c.id === transaction.cashboxId);
          if (cashbox) {
            setSelectedCashbox(cashbox);
            console.log("Otomatik kasa seçildi:", cashbox.name);
          } else {
            console.log("Kasa ID'si var ama kasa bulunamadı:", transaction.cashboxId);
            
            // Referans ID ile kasa hareketi ve kasa bilgisini bul
            await findCashboxByReferenceId();
          }
        } else {
          console.log("Kasa ID'si bulunamadı");
          // Referans ID ile kasa hareketi ve kasa bilgisini bul
          await findCashboxByReferenceId();
        }
      } else {
        // POS -> Nakit: Banka hareket kaydını bul
        if (transaction.bankAccountId) {
          // Banka hesabını otomatik seç
          const bankAccount = bankAccounts.find(b => b.id === transaction.bankAccountId);
          if (bankAccount) {
            setSelectedBankAccount(bankAccount);
            console.log("Otomatik banka hesabı seçildi:", bankAccount.bankName);
          } else {
            console.log("Banka hesap ID'si var ama hesap bulunamadı:", transaction.bankAccountId);
            
            // Referans ID ile banka hareketi ve banka bilgisini bul
            await findBankAccountByReferenceId();
          }
        } else {
          console.log("Banka hesap ID'si bulunamadı");
          
          // Referans ID ile banka hareketi ve banka bilgisini bul
          await findBankAccountByReferenceId();
        }
      }
      
      setLoading(false);
    } catch (err: any) {
      console.error("İlgili hareket kaydı bulunurken hata:", err);
      setLoading(false);
    }
  };
  
  // Referans ID ile kasa bulma (fallback). Veriler SQL'e tasindigindan eski RTDB islem-tablosu
  // taramasi kaldirildi; otomatik secim `transaction.cashboxId` uzerinden zaten yapiliyor.
  const findCashboxByReferenceId = async () => {
    if (!transaction || !companyId) return;
    if (transaction.cashboxId) {
      const cashbox = cashboxes.find(c => c.id === String(transaction.cashboxId));
      if (cashbox) setSelectedCashbox(cashbox);
    }
  };

  // Referans ID ile banka bulma (fallback). Veriler SQL'e tasindigindan eski RTDB islem-tablosu
  // taramasi kaldirildi; otomatik secim `transaction.bankAccountId` uzerinden zaten yapiliyor.
  const findBankAccountByReferenceId = async () => {
    if (!transaction || !companyId) return;
    if (transaction.bankAccountId) {
      const bankAccount = bankAccounts.find(b => b.id === String(transaction.bankAccountId));
      if (bankAccount) setSelectedBankAccount(bankAccount);
    }
  };
  
  // Kasaları yükle
  const loadCashboxes = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      
      // Kasaları SQL'den yükle (GET /api/Cashbox)
      const cashboxData = await getCashboxesMap(companyId!);
      
      if (cashboxData) {
        const cashboxList: Cashbox[] = [];
        
        Object.keys(cashboxData as any).forEach(id => {
          const data = (cashboxData as any)[id];
          // İşlem para birimiyle aynı kasaları filtrele
          if (data.currency === (transaction?.currency || 'TRY') && data.isActive !== false) {
            cashboxList.push({
              id,
              name: data.name || 'İsimsiz Kasa',
              balance: data.balance || 0,
              currency: data.currency || 'TRY',
              isActive: data.isActive !== false
            });
          }
        });
        
        setCashboxes(cashboxList.sort((a, b) => a.name.localeCompare(b.name)));
        
        // Transaction'da cashboxId varsa otomatik seç
        if (transaction?.cashboxId) {
          const selectedCashbox = cashboxList.find(c => c.id === transaction.cashboxId);
          if (selectedCashbox) {
            setSelectedCashbox(selectedCashbox);
            console.log("Transaction içinden kasa otomatik seçildi:", selectedCashbox.name);
          }
        }
      } else {
        setCashboxes([]);
      }
      
      setLoading(false);
    } catch (err: any) {
      console.error('Kasalar yüklenirken hata:', err);
      setError('Kasalar yüklenirken bir hata oluştu.');
      setLoading(false);
    }
  };
  
  // Banka hesaplarını yükle
  const loadBankAccounts = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      
      // Banka hesaplarını SQL'den yükle (GET /api/BankAccount)
      const accountsData = await getBankAccountsMap(companyId!);
      
      if (accountsData) {
        const accountsList: BankAccount[] = [];
        
        Object.keys(accountsData).forEach(id => {
          const data = (accountsData as any)[id];
          // İşlem para birimiyle aynı hesapları filtrele
          if (data.currency === (transaction?.currency || 'TRY') && data.isActive !== false) {
            accountsList.push({
              id,
              bankName: data.bankName || 'İsimsiz Banka',
              accountName: data.accountName,
              balance: data.balance || 0,
              currency: data.currency || 'TRY',
              isActive: data.isActive !== false
            });
          }
          
          // Alt hesapları da ekle
          if (data.hesaplar) {
            Object.keys(data.hesaplar).forEach(subId => {
              const subAccount = data.hesaplar[subId];
              if (subAccount.currency === (transaction?.currency || 'TRY') && subAccount.isActive !== false) {
                accountsList.push({
                  id: subId,
                  bankName: `${data.bankName} - ${subAccount.accountName || 'Alt Hesap'}`,
                  accountName: subAccount.accountName,
                  balance: subAccount.balance || 0,
                  currency: subAccount.currency || 'TRY',
                  isActive: subAccount.isActive !== false
                });
              }
            });
          }
        });
        
        setBankAccounts(accountsList.sort((a, b) => a.bankName.localeCompare(b.bankName)));
        
        // Transaction'da bankAccountId varsa otomatik seç
        if (transaction?.bankAccountId) {
          const selectedBankAccount = accountsList.find(b => b.id === transaction.bankAccountId);
          if (selectedBankAccount) {
            setSelectedBankAccount(selectedBankAccount);
            console.log("Transaction içinden banka hesabı otomatik seçildi:", selectedBankAccount.bankName);
          }
        }
      } else {
        setBankAccounts([]);
      }
      
      setLoading(false);
    } catch (err: any) {
      console.error('Banka hesapları yüklenirken hata:', err);
      setError('Banka hesapları yüklenirken bir hata oluştu.');
      setLoading(false);
    }
  };
  
  // Satış ekranlarını yükle (SQL — Hızlı Satış + PYK)
  // NOT: eski RTDB `pos_devices` düğümünün temiz bir SQL karşılığı yok ve birincil kaynak
  //      işlem kaydındaki screenName olduğundan bu liste yalnız isim-arama yedeğidir.
  const loadPosDevices = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const screensArray = await getSalesScreenOptions(companyId!);
      console.log(`${screensArray.length} satış ekranı bulundu (SQL)`);
      setPosDevices(screensArray);
      setLoading(false);
    } catch (err: any) {
      console.error('Satış ekranları yüklenirken hata:', err);
      setError('Satış ekranları yüklenirken bir hata oluştu.');
      setLoading(false);
    }
  };
  
  // Para birimini formatla
  const formatCurrency = (amount: number, currencyCode: string = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  // Kullanıcı adını oluştur
  const getUserFullName = (): string => {
    if (userDetails && userDetails.firstName && userDetails.lastName) {
      return `${userDetails.firstName} ${userDetails.lastName}`;
    } else if (userDetails && userDetails.firstName) {
      return userDetails.firstName;
    } else if (userDetails && userDetails.email) {
      return userDetails.email;
    }
    return 'Sistem';
  };
  
  // İşlem günlüğüne adım ekle - fonksiyonu dışarıda tanımla
  const addLog = (message: string) => {
    setProcessLog(prev => [...prev, message]);
  };
  
  // İleri butonuna tıklandığında
  const handleNext = () => {
    // Geçerli adımda gerekli kontroller
    if (activeStep === 0) {
      // İlk adımda sadece bilgi gösterimi var, kontrol yok
      setActiveStep(1);
    } else if (activeStep === 1) {
      // Hedef hesap seçimi kontrolü
      if (targetPaymentMethod === 'cash' && !selectedCashbox) {
        setError('Lütfen bir kasa seçin.');
        return;
      } else if (targetPaymentMethod === 'pos' && !selectedBankAccount) {
        setError('Lütfen bir banka hesabı seçin.');
        return;
      }
      setActiveStep(2);
    } else if (activeStep === 2) {
      // Onay adımından sonra işlemi başlat
      handleProcessTransaction();
    } else if (activeStep === 3 && isSuccess) {
      // İşlem tamamlandı, modalı kapat
      if (onSuccess) onSuccess();
      onClose();
    }
  };
  
  // Geri butonuna tıklandığında
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError(null);
  };
  
  // İşlem gerçekleştirme - SQL API
  const handleProcessTransaction = async () => {
    if (!transaction || !companyId) {
      setError('İşlem bilgileri eksik.');
      return;
    }
    
    // Nakit -> POS: Kasa ve banka hesabı kontrolü
    if (targetPaymentMethod === 'pos' && !selectedBankAccount) {
      setError('Lütfen bir banka hesabı seçin.');
      return;
    }
    
    // POS -> Nakit: Banka hesabı ve kasa kontrolü
    if (targetPaymentMethod === 'cash' && !selectedCashbox) {
      setError('Lütfen bir kasa seçin.');
      return;
    }
    
    setActiveStep(3); // İşlem adımına geç
    setLoading(true);
    setError(null);
    setProcessLog([]); // İşlem loglarını temizle
    
    try {
      // İşlem günlüğüne adım ekle
      addLog('İşlem başlatılıyor...');
      
      // Kullanıcı bilgisi
      const userFullName = getUserFullName();
      
      // Ödeme yöntemi isimlerini al
      const currentMethodName = PAYMENT_METHOD_NAMES[currentPaymentMethod] || currentPaymentMethod;
      const targetMethodName = PAYMENT_METHOD_NAMES[targetPaymentMethod] || targetPaymentMethod;
      
      // İşlem açıklaması
      const operationDescription = description || 
        `${transaction.accountName} carisi için ödeme yöntemi değişimi: ${currentMethodName} -> ${targetMethodName}`;
      
      addLog(`Ödeme yöntemi değiştiriliyor: ${currentMethodName} -> ${targetMethodName}`);
      
      // Transaction ID kontrolü - Sayısal ise SQL, değilse Firebase
      const numericTransactionId = parseInt(transaction.id, 10);
      const isNumericId = !isNaN(numericTransactionId) && numericTransactionId.toString() === transaction.id;
      
      if (isNumericId) {
        // SQL ID - SQL API'ye istek gönder
        addLog('SQL API üzerinden işlem yapılıyor...');
        
        const changePaymentMethodPayload = {
          companyId: companyId || '', // ✅ MULTI-TENANT: companyId eklendi
          newPaymentMethod: targetPaymentMethod,
          newCashboxId: targetPaymentMethod === 'cash' ? selectedCashbox?.id || null : null,
          newBankAccountId: targetPaymentMethod === 'pos' || targetPaymentMethod === 'bank_transfer' ? selectedBankAccount?.id || null : null,
          reason: operationDescription,
          changedBy: userFullName || 'Sistem'
        };

        addLog('API isteği gönderiliyor...');
        const response = await accountingApi.post(ACCOUNTING_API.TRANSACTION_CHANGE_PAYMENT_METHOD(numericTransactionId), changePaymentMethodPayload);

        const result = response.data;
        addLog('Ödeme yöntemi başarıyla değiştirildi!');
        addLog('İşlem başarıyla tamamlandı!');
        setIsSuccess(true);
      } else {
        // Firebase ID - Firebase ile devam et
        addLog('Firebase ID tespit edildi. Firebase üzerinden işlem yapılıyor...');
        addLog('⚠️ Bu işlem için önce Firebase verilerini SQL\'e taşımanız önerilir.');
        
        // Firebase mantığı burada devam edebilir, ancak kullanıcıya uyarı verelim
        throw new Error('Firebase ID\'li işlemler için lütfen önce verileri SQL\'e taşıyın.');
      }
      
    } catch (err: any) {
      console.error('İşlem sırasında hata:', err);
      setError(`İşlem sırasında hata oluştu: ${err.message}`);
      addLog(`HATA: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Adım içeriğini render et
  const getStepContent = (step: number) => {
    // Transaction yoksa hata göster (tüm adımlar için geçerli)
    if (!transaction && (step === 0 || step === 1 || step === 2)) {
      return (
        <Alert severity="error">
          İşlem bilgileri yüklenemedi. Lütfen tekrar deneyin.
        </Alert>
      );
    }
    
    // Ödeme yöntemi değerlerini kontrol et (ortak kullanım için)
    const displayCurrentMethod = transaction 
      ? (PAYMENT_METHOD_NAMES[currentPaymentMethod] || currentPaymentMethod || '-')
      : '-';
    
    const displayTargetMethod = transaction 
      ? (PAYMENT_METHOD_NAMES[targetPaymentMethod] || targetPaymentMethod || '-')
      : '-';

    switch (step) {
      case 0: // İşlem bilgilerini kontrol et
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom>İşlem Bilgileri</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Cari Hesap:</Typography>
                  <Typography variant="body1">{transaction?.accountName || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">İşlem Tutarı:</Typography>
                  <Typography variant="body1">
                    {transaction ? formatCurrency(transaction.amount, transaction.currency) : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Mevcut Ödeme Yöntemi:</Typography>
                  <Typography variant="body1">{displayCurrentMethod}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Hedef Ödeme Yöntemi:</Typography>
                  <Typography variant="body1">{displayTargetMethod}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">İşlem Tarihi:</Typography>
                  <Typography variant="body1">{transaction?.date || transaction?.createdAt || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">İşlem Açıklaması:</Typography>
                  <Typography variant="body1">{transaction?.description || '-'}</Typography>
                </Grid>
              </Grid>
            </Paper>
            <Alert severity="info" sx={{ mb: 2 }}>
              Bu işlem, ödeme yöntemini <strong>{displayCurrentMethod}</strong> 'den <strong>{displayTargetMethod}</strong> 'e dönüştürecektir.
            </Alert>
          </Box>
        );
      
      case 1: // Hedef hesabı seç
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom>Hedef Hesap Seçimi</Typography>
            
            {targetPaymentMethod === 'cash' ? (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  İşlem tutarı ({transaction ? formatCurrency(transaction.amount, transaction.currency) : ''}) nakit olarak kaydedilecek.
                </Alert>
                
                {/* Otomatik tespit edilen banka hesabı - değiştirilemez */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Kaynak Banka Hesabı (Mevcut İşlem)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      {selectedBankAccount ? (
                        <>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedBankAccount.bankName}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Bakiye: {formatCurrency(selectedBankAccount.balance, selectedBankAccount.currency)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="error">
                          Banka hesabı bulunamadı. Lütfen yöneticinizle iletişime geçin.
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ ml: 2, p: 1, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 1 }}>
                      <Typography variant="caption">Otomatik Tespit Edildi</Typography>
                    </Box>
                  </Box>
                </Paper>
                
                {/* Kullanıcının seçeceği hedef kasa */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Hedef Kasa Seçin</InputLabel>
                  <Select
                    value={selectedCashbox?.id || ''}
                    onChange={(e) => {
                      const cashbox = cashboxes.find(c => c.id === e.target.value);
                      setSelectedCashbox(cashbox || null);
                    }}
                    label="Hedef Kasa Seçin"
                  >
                    <MenuItem value="" disabled>Kasa seçin</MenuItem>
                    {cashboxes.map((cashbox) => (
                      <MenuItem key={cashbox.id} value={cashbox.id}>
                        {cashbox.name} - Bakiye: {formatCurrency(cashbox.balance, cashbox.currency)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  İşlem tutarı ({transaction ? formatCurrency(transaction.amount, transaction.currency) : ''}) POS ile ödenecek.
                </Alert>
                
                {/* Otomatik tespit edilen kasa - değiştirilemez */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Kaynak Kasa (Mevcut İşlem)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      {selectedCashbox ? (
                        <>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedCashbox.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Bakiye: {formatCurrency(selectedCashbox.balance, selectedCashbox.currency)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="error">
                          Kasa bulunamadı. Lütfen yöneticinizle iletişime geçin.
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ ml: 2, p: 1, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 1 }}>
                      <Typography variant="caption">Otomatik Tespit Edildi</Typography>
                    </Box>
                  </Box>
                </Paper>
                
                {/* Kullanıcının seçeceği hedef banka hesabı */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Hedef Banka Hesabı Seçin</InputLabel>
                  <Select
                    value={selectedBankAccount?.id || ''}
                    onChange={(e) => {
                      const bankAccount = bankAccounts.find(b => b.id === e.target.value);
                      setSelectedBankAccount(bankAccount || null);
                    }}
                    label="Hedef Banka Hesabı Seçin"
                  >
                    <MenuItem value="" disabled>Banka hesabı seçin</MenuItem>
                    {bankAccounts.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.bankName} - Bakiye: {formatCurrency(account.balance, account.currency)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            
            {/* Satış Ekranı Seçimi */}
            <Autocomplete
              fullWidth
              size="small"
              options={posDevices}
              getOptionLabel={(option) => option.name}
              value={selectedPosDevice}
              onChange={(event, newValue) => {
                setSelectedPosDevice(newValue);
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Satış Ekranı Seçin" 
                  placeholder="Satış ekranı seçin"
                  helperText="Seçilen satış ekranı bilgisi, hareket kayıtlarına eklenecektir"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography variant="body2">
                    {option.name}
                  </Typography>
                </li>
              )}
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="İşlem Açıklaması (İsteğe bağlı)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="Ödeme yöntemi değişimi açıklaması"
            />
          </Box>
        );
      
      case 2: // Onay
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom>İşlem Onayı</Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Bu işlem geri alınamaz. Devam etmek istediğinizden emin misiniz?
            </Alert>
            
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>İşlem Özeti</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Cari Hesap:</Typography>
                  <Typography variant="body1" fontWeight="medium">{transaction?.accountName || '-'}</Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">İşlem Tutarı:</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {transaction ? formatCurrency(transaction.amount, transaction.currency) : '-'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Değişiklik:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
                    <Box sx={{ 
                      bgcolor: 'error.light', 
                      color: 'error.contrastText', 
                      p: 1, 
                      borderRadius: 1,
                      display: 'inline-block',
                      mr: 1
                    }}>
                      {displayCurrentMethod}
                    </Box>
                    <ArrowRightAltIcon sx={{ mx: 1 }} />
                    <Box sx={{ 
                      bgcolor: 'success.light', 
                      color: 'success.contrastText', 
                      p: 1, 
                      borderRadius: 1,
                      display: 'inline-block' 
                    }}>
                      {displayTargetMethod}
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                
                {currentPaymentMethod === 'cash' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Kaynak (Azaltılacak):</Typography>
                      <Typography variant="body1" color="error.main" fontWeight="medium">
                        Kasa: {selectedCashbox?.name || '-'} 
                        {selectedCashbox && ` (${formatCurrency(selectedCashbox.balance, selectedCashbox.currency)})`}
                      </Typography>
                      {transaction?.cashboxName && transaction.cashboxName !== selectedCashbox?.name && (
                        <Typography variant="caption" color="text.secondary">
                          Not: Orijinal işlem "{transaction.cashboxName}" kasasında yapılmıştı.
                        </Typography>
                      )}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Hedef (Artırılacak):</Typography>
                      <Typography variant="body1" color="success.main" fontWeight="medium">
                        Banka: {selectedBankAccount?.bankName || '-'}
                        {selectedBankAccount && ` (${formatCurrency(selectedBankAccount.balance, selectedBankAccount.currency)})`}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                {currentPaymentMethod !== 'cash' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Kaynak (Azaltılacak):</Typography>
                      <Typography variant="body1" color="error.main" fontWeight="medium">
                        Banka: {selectedBankAccount?.bankName || '-'}
                        {selectedBankAccount && ` (${formatCurrency(selectedBankAccount.balance, selectedBankAccount.currency)})`}
                      </Typography>
                      {transaction?.bankAccountName && transaction.bankAccountName !== selectedBankAccount?.bankName && (
                        <Typography variant="caption" color="text.secondary">
                          Not: Orijinal işlem "{transaction.bankAccountName}" hesabında yapılmıştı.
                        </Typography>
                      )}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Hedef (Artırılacak):</Typography>
                      <Typography variant="body1" color="success.main" fontWeight="medium">
                        Kasa: {selectedCashbox?.name || '-'}
                        {selectedCashbox && ` (${formatCurrency(selectedCashbox.balance, selectedCashbox.currency)})`}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                {selectedPosDevice && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Satış Ekranı:</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedPosDevice.name}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                
                {description && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Açıklama:</Typography>
                    <Typography variant="body1">{description}</Typography>
                  </Grid>
                )}
                
                {!description && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Oluşturulacak Açıklama:</Typography>
                    <Typography variant="body1" fontStyle="italic">
                      {transaction?.accountName} carisi için ödeme yöntemi değişimi: {displayCurrentMethod} -&gt; {displayTargetMethod}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        );
      
      case 3: // İşlem tamamlanıyor
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom>İşlem Durumu</Typography>
            
            {isSuccess ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                İşlem başarıyla tamamlandı!
              </Alert>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : loading ? (
              <Box display="flex" justifyContent="center" mb={2}>
                <CircularProgress />
              </Box>
            ) : null}
            
            <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: '300px', overflowY: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>İşlem Logları</Typography>
              {processLog.map((log, index) => (
                <Typography key={index} variant="body2" sx={{ py: 0.5 }}>
                  {log}
                </Typography>
              ))}
            </Paper>
          </Box>
        );
      
      default:
        return 'Bilinmeyen adım';
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <SyncAltIcon sx={{ mr: 1 }} />
            <Typography variant="h6">İşlem Hareketini Değiştir</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <GuideButton guideKey="finance.transactionPaymentMethodChange" />
            <IconButton edge="end" color="inherit" onClick={onClose} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ py: 2, mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && activeStep !== 3 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {getStepContent(activeStep)}
      </DialogContent>
      
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={loading}
        >
          {isSuccess ? 'Kapat' : 'İptal'}
        </Button>
        
        <Box sx={{ flex: '1 1 auto' }} />
        
        {activeStep > 0 && activeStep < 3 && (
          <Button onClick={handleBack} disabled={loading}>
            Geri
          </Button>
        )}
        
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={
            loading || 
            (activeStep === 1 && targetPaymentMethod === 'cash' && !selectedCashbox) ||
            (activeStep === 1 && targetPaymentMethod === 'pos' && !selectedBankAccount)
          }
        >
          {activeStep === steps.length - 1 ? 'Bitir' :
           activeStep === 2 ? 'İşlemi Başlat' : 'Devam'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionPaymentMethodChangeModal; 